// Turning the citation form's fields into patches for pictures already on a
// slide.
//
// A picture pasted onto a canvas slide is placed first and cited second, so
// the citation dialog is not building a block — it is patching one that
// already exists. This module is that translation: the form's flat
// `image_<n>_<field>` values in, one patch per picture out, keyed by block id.
//
// Kept separate from the modal because it is twenty-odd field names copied
// from one shape to another, which is exactly the kind of code where a typo
// silently drops an author's work and no amount of clicking through the UI
// will reliably catch it.

/**
 * One picture's patch.
 *
 * Every field is optional. A dialog closed or saved without a thing typed into
 * it yields a patch that changes nothing meaningful, which is what makes
 * "citations are optional" true rather than merely intended.
 *
 * @param {object} formData  the modal's flat form state
 * @param {number} index     the picture's position in the paste
 * @param {object} image     { id, name } for the placed block
 */
/**
 * The fields that, between them, decide whether a picture has been cited.
 * Presentational settings (caption, alt, the include toggles) are absent on
 * purpose — a caption is not a citation.
 */
const CITATION_CONTENT_FIELDS = [
    'title', 'author', 'source', 'url', 'date', 'page',
    'license', 'tableNumber', 'referenceCategory', 'medium',
    'referenceTitle', 'articleTitle'
];

export const citationUpdateFromForm = (formData = {}, index = 0, image = {}) => {
    const field = (name) => formData[`image_${index}_${name}`];
    const caption = field('caption');

    /**
     * Whether anything was actually filled in.
     *
     * This is what keeps the citation genuinely optional. An image block that
     * says it has a citation but carries no fields renders the stub note
     * "Note. From n.d." beneath the picture — so a picture pasted and left
     * alone would wear a meaningless caption forever. Citing is therefore
     * something the author opts into by typing, not by declining to.
     */
    const hasCitationContent = CITATION_CONTENT_FIELDS
        .some((name) => String(field(name) ?? '').trim() !== '');

    return {
        id: image.id,
        // Falls back to the picture's own name so a saved image never ends up
        // with empty alt text.
        alt: field('alt') || image.name || '',
        // Matches the wrapping the image modal applies, so a pasted picture's
        // caption renders identically to one added any other way.
        caption: caption ? `<strong>Figure:</strong> ${caption}` : '',
        imageShowCaption: field('showCaption') !== false,
        imageTitle: field('title') || '',
        imageAuthor: field('author') || '',
        imageSource: field('source') || '',
        imageUrl: field('url') || '',
        imageDate: field('date') || '',
        imageSourceType: field('sourceType') || 'image',
        imagePage: field('page') || '',
        imageIncludePage: field('includePage') !== false,
        imageLicense: field('license') || '',
        imageIncludeLicense: field('includeLicense') !== false,
        imageTableNumber: field('tableNumber') || '',
        imageIsAdapted: Boolean(field('isAdapted')),
        // Both have to hold: something to cite, and the author not having
        // unticked the box.
        imageIncludeCitation: hasCitationContent && field('includeCitation') !== false,
        imageReferenceCategory: field('referenceCategory') || '',
        imageMedium: field('medium') || '',
        imageReferenceTitle: field('referenceTitle') || '',
        imageArticleTitle: field('articleTitle') || ''
    };
};

/** A patch for every picture the paste placed, in paste order. */
export const pastedImageUpdates = (formData = {}) => (
    Array.isArray(formData.pastedImages) ? formData.pastedImages : []
).map((image, index) => citationUpdateFromForm(formData, index, image));

/**
 * Apply the patches to a section's blocks, matching on block id.
 *
 * Blocks the paste did not create are returned untouched, and a patch whose
 * block has since been deleted is simply dropped — an author can delete a
 * pasted picture while the dialog is still open, and that should not resurrect
 * it or throw.
 */
export const applyPastedImageUpdates = (blocks, updates) => {
    const byId = new Map((updates || []).filter((update) => update?.id).map((update) => [update.id, update]));
    if (!byId.size) return blocks || [];

    return (blocks || []).map((block) => {
        const update = byId.get(block?.id);
        if (!update) return block;
        const { id, ...fields } = update;
        return { ...block, ...fields };
    });
};
